import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { goEntitiesApi } from "@/services/go-entities.service";
import type { GoEntityListItem } from "@/types/go-entity";
import type { GoEntityCompareRowStatus } from "@/types/go-entity-compare";
import { cn } from "@/lib/utils";

function formatValue(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function statusLabel(status: GoEntityCompareRowStatus): string {
  switch (status) {
    case "equal":
      return "совпадает";
    case "diff":
      return "отличается";
    case "left_only":
      return "только слева";
    case "right_only":
      return "только справа";
    default:
      return status;
  }
}

function statusClass(status: GoEntityCompareRowStatus): string {
  switch (status) {
    case "equal":
      return "text-muted-foreground";
    case "diff":
      return "text-amber-700 dark:text-amber-400 font-medium";
    case "left_only":
    case "right_only":
      return "text-sky-700 dark:text-sky-400";
    default:
      return "";
  }
}

export interface GoEntityComparePanelProps {
  leftId: number | null;
  rightId: number | null;
  onRightIdChange: (id: number | null) => void;
  entities: GoEntityListItem[];
}

export function GoEntityComparePanel({
  leftId,
  rightId,
  onRightIdChange,
  entities,
}: GoEntityComparePanelProps) {
  const compareQuery = useQuery({
    queryKey: ["go-entities", "compare", leftId, rightId],
    queryFn: () => goEntitiesApi.compare(leftId!, rightId!),
    enabled: leftId != null && rightId != null && leftId !== rightId,
  });

  const rightOptions = entities.filter((e) => e.ginf_obj_id !== leftId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Сравнение сущностей ГО</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label>Слева (текущая)</Label>
            <div className="rounded-md border px-3 py-2 text-foreground">
              {leftId != null
                ? entities.find((e) => e.ginf_obj_id === leftId)?.obj_name || `#${leftId}`
                : "Выберите сущность слева в списке"}
            </div>
          </div>
          <div className="space-y-1">
            <Label>Справа</Label>
            <Select
              value={rightId != null ? String(rightId) : "none"}
              onValueChange={(v) => onRightIdChange(v === "none" ? null : Number(v))}
              disabled={leftId == null}
            >
              <SelectTrigger>
                <SelectValue placeholder="Выберите вторую сущность" />
              </SelectTrigger>
              <SelectContent className="z-[250]">
                <SelectItem value="none">Не выбрано</SelectItem>
                {rightOptions.map((item) => (
                  <SelectItem key={item.ginf_obj_id} value={String(item.ginf_obj_id)}>
                    {item.obj_name || `#${item.ginf_obj_id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {leftId == null && (
          <p className="text-muted-foreground">Сначала выберите сущность в реестре слева.</p>
        )}
        {leftId != null && rightId == null && (
          <p className="text-muted-foreground">Выберите вторую сущность для сравнения.</p>
        )}
        {compareQuery.isLoading && <p className="text-muted-foreground">Загрузка сравнения…</p>}
        {compareQuery.isError && (
          <p className="text-destructive">
            Не удалось сравнить: {(compareQuery.error as Error)?.message || "ошибка"}
          </p>
        )}
        {compareQuery.data && (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-2 font-medium">Поле</th>
                  <th className="px-3 py-2 font-medium">
                    {compareQuery.data.left.obj_name || `#${compareQuery.data.left.ginf_obj_id}`}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {compareQuery.data.right.obj_name || `#${compareQuery.data.right.ginf_obj_id}`}
                  </th>
                  <th className="px-3 py-2 font-medium">Статус</th>
                </tr>
              </thead>
              <tbody>
                {compareQuery.data.rows.map((row) => (
                  <tr key={row.key} className="border-t">
                    <td className="px-3 py-2 text-foreground">{row.label}</td>
                    <td className="px-3 py-2 whitespace-pre-wrap">{formatValue(row.left_value)}</td>
                    <td className="px-3 py-2 whitespace-pre-wrap">{formatValue(row.right_value)}</td>
                    <td className={cn("px-3 py-2", statusClass(row.status))}>
                      {statusLabel(row.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
